import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { mockPartners } from '../../mocks/partners';
import RelatedLinks from '../../components/feature/RelatedLinks';

export default function HomePage() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { user } = useAuth();

  // スクロール監視
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const features = [
    {
      icon: 'ri-chat-heart-line',
      title: 'みんなの休憩室',
      description: '匿名で安心して悩みを共有できる掲示板。「わかるよ」と言ってもらえて救われました。同じ悩みを持つ仲間がいると知れただけで、心が軽くなりました。',
      color: 'rose'
    },
    {
      icon: 'ri-book-open-line',
      title: 'お守りマニュアル',
      description: '入園準備や緊急時対応など、先輩ママの知恵が詰まった実践的なガイド集。',
      color: 'amber'
    },
    {
      icon: 'ri-user-star-line',
      title: 'スマイル・パートナー',
      description: '看護師、保育士、先輩ママがあなたに寄り添い、共感とアドバイスを提供します。',
      color: 'purple'
    }
  ];

  const voices = [
    {
      name: '佐藤 恵子さん',
      role: '3歳児のママ',
      comment: '夜中に一人で泣いていた時、ここで「わかるよ」と言ってもらえて救われました。同じ悩みを持つ仲間がいると知れただけで、心が軽くなりました。',
      avatar: 'https://public.readdy.ai/ai/img_res/4290264835f1b78e8841f71ac285ed79.jpg'
    },
    {
      name: '田中 美咲さん',
      role: '5歳児のママ',
      comment: 'スマイル・パートナーの看護師さんが、医療的ケアの不安に丁寧に答えてくれました。専門家の意見を聞けて、自信を持ってケアできるようになりました。',
      avatar: 'https://readdy.ai/api/search-image?query=Confident%20Japanese%20mother%20in%20her%2030s%20with%20reassuring%20smile%20soft%20natural%20lighting%20clean%20white%20background%20portrait%20style&width=200&height=200&seq=voice2&orientation=squarish'
    },
    {
      name: '鈴木 愛さん',
      role: '2歳児のママ',
      comment: 'お守りマニュアルの「先輩ママの1日スケジュール」を見て、自分だけが大変なわけじゃないと分かりました。具体的な工夫も参考になっています。',
      avatar: 'https://readdy.ai/api/search-image?query=Hopeful%20Japanese%20mother%20in%20her%2030s%20with%20peaceful%20expression%20soft%20natural%20lighting%20clean%20white%20background%20portrait%20style&width=200&height=200&seq=voice3&orientation=squarish'
    }
  ];

  // 匿名ユーザーアイコン（人物画像）
  const anonymousIcons = [
    'https://readdy.ai/api/search-image?query=Friendly%20Japanese%20woman%20smiling%20warmly%20soft%20pastel%20background%20gentle%20lighting%20portrait%20style%20avatar&width=100&height=100&seq=user-avatar-1&orientation=squarish',
    'https://readdy.ai/api/search-image?query=Kind%20Japanese%20mother%20with%20caring%20expression%20soft%20pastel%20background%20gentle%20lighting%20portrait%20style%20avatar&width=100&height=100&seq=user-avatar-2&orientation=squarish',
    'https://readdy.ai/api/search-image?query=Supportive%20Japanese%20woman%20with%20gentle%20smile%20soft%20pastel%20background%20gentle%20lighting%20portrait%20style%20avatar&width=100&height=100&seq=user-avatar-3&orientation=squarish',
    'https://readdy.ai/api/search-image?query=Warm%20Japanese%20mother%20with%20peaceful%20expression%20soft%20pastel%20background%20gentle%20lighting%20portrait%20style%20avatar&width=100&height=100&seq=user-avatar-4&orientation=squarish',
  ];

  const partnerCategories = [
    {
      title: '看護師',
      description: '医療の専門知識で健康面をサポート',
      icon: 'ri-nurse-line',
      color: 'from-teal-500 to-cyan-500',
      role: 'nurse',
      count: 2
    },
    {
      title: '保育士',
      description: '子育ての実践的なアドバイス',
      icon: 'ri-parent-line',
      color: 'from-pink-500 to-rose-500',
      role: 'childcare_worker',
      count: 2
    },
    {
      title: '先輩ママ',
      description: '経験に基づいた共感とサポート',
      icon: 'ri-heart-line',
      color: 'from-purple-500 to-pink-500',
      role: 'senior_parent',
      count: 3
    },
    {
      title: '理学療法士',
      description: '発達と運動面の専門サポート',
      icon: 'ri-run-line',
      color: 'from-blue-500 to-indigo-500',
      role: 'physical_therapist',
      count: 2
    },
    {
      title: '社会福祉士',
      description: '福祉制度と生活支援の相談',
      icon: 'ri-hand-heart-line',
      color: 'from-amber-500 to-orange-500',
      role: 'social_worker',
      count: 2
    }
  ];

  // パートナーを役職ごとにグループ化
  const nursePartners = mockPartners.filter(p => p.partner_role === '看護師');
  const teacherPartners = mockPartners.filter(p => p.partner_role === '保育士');
  const momPartners = mockPartners.filter(p => p.partner_role === '先輩ママ');
  const ptPartners = mockPartners.filter(p => p.partner_role === '理学療法士');
  const swPartners = mockPartners.filter(p => p.partner_role === '社会福祉士');

  const handlePartnerLinkClick = () => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  };

  return (
    <div className="min-h-screen bg-white">
      {/* ナビゲーション */}
      <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-transparent'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 sm:gap-3">
            <div className="w-8 h-8 sm:w-10 sm:h-10 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center">
              <i className="ri-ship-line text-white text-lg sm:text-xl"></i>
            </div>
            <span className={`text-base sm:text-xl font-bold transition-colors ${isScrolled ? 'text-gray-900' : 'text-white'}`}>みやぎスマイル・ポート</span>
          </Link>
          
          {/* デスクトップメニュー */}
          <div className="hidden md:flex items-center gap-6">
            <a href="#home" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-900 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>ホーム</a>
            <a href="#about" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-900 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>スマイル・ポートとは</a>
            
            {/* みんなの休憩室 */}
            <Link to="/board" className={`group relative px-4 py-2 rounded-xl transition-all cursor-pointer ${isScrolled ? 'hover:bg-rose-50' : 'hover:bg-white/10'}`}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${isScrolled ? 'bg-rose-100' : 'bg-white/20'}`}>
                  <i className={`ri-chat-3-line text-base ${isScrolled ? 'text-rose-600' : 'text-white'}`}></i>
                </div>
                <div>
                  <div className={`text-sm font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>みんなの休憩室</div>
                  <div className={`text-xs ${isScrolled ? 'text-gray-600' : 'text-white/80'}`}>匿名のお悩み掲示板</div>
                </div>
              </div>
            </Link>

            {/* お守りマニュアル */}
            <Link to="/manual" className={`group relative px-4 py-2 rounded-xl transition-all cursor-pointer ${isScrolled ? 'hover:bg-amber-50' : 'hover:bg-white/10'}`}>
              <div className="flex items-center gap-2">
                <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${isScrolled ? 'bg-amber-100' : 'bg-white/20'}`}>
                  <i className={`ri-book-open-line text-base ${isScrolled ? 'text-amber-600' : 'text-white'}`}></i>
                </div>
                <div>
                  <div className={`text-sm font-bold ${isScrolled ? 'text-gray-900' : 'text-white'}`}>お守りマニュアル</div>
                  <div className={`text-xs ${isScrolled ? 'text-gray-600' : 'text-white/80'}`}>専門家の推奨マニュアル</div>
                </div>
              </div>
            </Link>

            <a href="#voices" className={`text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${isScrolled ? 'text-gray-900 hover:text-teal-600' : 'text-white hover:text-teal-200'}`}>利用者の声</a>
            {user ? (
              <Link to="/mypage" className={`flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full hover:shadow-lg transition-all cursor-pointer whitespace-nowrap ${isScrolled ? '' : 'shadow-lg'}`}>
                <i className="ri-user-smile-line text-sm"></i>
                マイページ
              </Link>
            ) : (
              <Link to="/auth/login" className={`px-6 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full hover:shadow-lg transition-all cursor-pointer whitespace-nowrap ${isScrolled ? '' : 'shadow-lg'}`}>
                ログイン
              </Link>
            )}
          </div>

          {/* モバイルハンバーガーメニュー */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center cursor-pointer ${isScrolled ? 'text-gray-900' : 'text-white'}`}
          >
            <i className={`${isMobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'} text-2xl`}></i>
          </button>
        </div>

        {/* モバイルメニュー */}
        {isMobileMenuOpen && (
          <div className="md:hidden bg-white shadow-lg">
            <div className="px-4 py-3 space-y-3">
              <a href="#home" onClick={() => setIsMobileMenuOpen(false)} className="block text-sm font-medium text-gray-900 hover:text-teal-600 cursor-pointer">ホーム</a>
              <a href="#about" onClick={() => setIsMobileMenuOpen(false)} className="block text-sm font-medium text-gray-900 hover:text-teal-600 cursor-pointer">スマイル・ポートとは</a>
              
              {/* みんなの休憩室 */}
              <Link to="/board" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 p-3 rounded-xl hover:bg-rose-50 transition-all cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center bg-rose-100 rounded-lg">
                  <i className="ri-chat-3-line text-rose-600 text-lg"></i>
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900">みんなの休憩室</div>
                  <div className="text-xs text-gray-600">匿名のお悩み掲示板</div>
                </div>
              </Link>

              {/* お守りマニュアル */}
              <Link to="/manual" onClick={() => setIsMobileMenuOpen(false)} className="flex items-center gap-3 p-3 rounded-xl hover:bg-amber-50 transition-all cursor-pointer">
                <div className="w-10 h-10 flex items-center justify-center bg-white/20 rounded-lg">
                  <i className="ri-book-open-line text-amber-600 text-lg"></i>
                </div>
                <div>
                  <div className="text-sm font-bold text-gray-900">お守りマニュアル</div>
                  <div className="text-xs text-gray-600">専門家の推奨マニュアル</div>
                </div>
              </Link>

              <a href="#voices" onClick={() => setIsMobileMenuOpen(false)} className="block text-sm font-medium text-gray-900 hover:text-teal-600 cursor-pointer">利用者の声</a>
              {user ? (
                <Link to="/mypage" onClick={() => setIsMobileMenuOpen(false)} className="block px-6 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full text-center cursor-pointer">
                  マイページ
                </Link>
              ) : (
                <Link to="/auth/login" onClick={() => setIsMobileMenuOpen(false)} className="block px-6 py-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white rounded-full text-center cursor-pointer">
                  ログイン
                </Link>
              )}
            </div>
          </div>
        )}
      </nav>

      {/* ヒーローセクション */}
      <section id="home" className="relative min-h-screen flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 w-full h-full">
          <img 
            src="https://readdy.ai/api/search-image?query=Warm%20supportive%20hands%20reaching%20together%20in%20gentle%20pastel%20colors%20soft%20golden%20light%20peaceful%20atmosphere%20caring%20community%20connection%20minimalist%20artistic%20style%20with%20teal%20and%20rose%20tones&width=1920&height=1080&seq=hero-hands&orientation=landscape" 
            alt="スマイル・ポート" 
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-teal-900/50 via-teal-800/40 to-rose-900/50"></div>
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 w-full text-center">
          <div className="flex items-center justify-center gap-3 mb-6 sm:mb-8">
            <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center">
              <i className="ri-ship-line text-white text-xl sm:text-2xl"></i>
            </div>
          </div>
          
          <h1 className="text-3xl sm:text-5xl lg:text-7xl font-bold text-white mb-4 sm:mb-6 leading-tight">
            みやぎスマイル・ポート
          </h1>
          
          <p className="text-xl sm:text-2xl lg:text-3xl text-white/90 mb-3 sm:mb-4 font-light">
            〜医療的ケア児と家族の港〜
          </p>
          
          <p className="text-base sm:text-lg lg:text-xl text-white/80 mb-8 sm:mb-12 max-w-3xl mx-auto leading-relaxed px-4">
            ここはあなたの避難所です。否定されることはありません。<br className="hidden sm:block" />
            一人じゃない。同じ悩みを持つ仲間と、寄り添う専門家がいます。
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4">
            <Link to="/board" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-white text-gray-900 px-5 sm:px-6 py-3 sm:py-4 rounded-2xl hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap shadow-xl group">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-rose-100 rounded-xl">
                <i className="ri-chat-3-line text-rose-600 text-lg sm:text-xl"></i>
              </div>
              <div className="text-left">
                <div className="text-base sm:text-lg font-bold">みんなの休憩室へ</div>
                <div className="text-xs sm:text-sm text-gray-600">匿名のお悩み掲示板</div>
              </div>
              <i className="ri-arrow-right-line text-lg sm:text-xl text-gray-400 group-hover:text-gray-600 transition-colors"></i>
            </Link>
            <Link to="/manual" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-white/10 backdrop-blur-sm border-2 border-white text-white px-5 sm:px-6 py-3 sm:py-4 rounded-2xl hover:bg-white/20 transition-all cursor-pointer whitespace-nowrap group">
              <div className="w-10 h-10 sm:w-12 sm:h-12 flex items-center justify-center bg-white/20 rounded-xl">
                <i className="ri-book-open-line text-white text-lg sm:text-xl"></i>
              </div>
              <div className="text-left">
                <div className="text-base sm:text-lg font-bold">お守りマニュアル</div>
                <div className="text-xs sm:text-sm text-white/80">専門家の推奨マニュアル</div>
              </div>
              <i className="ri-arrow-right-line text-lg sm:text-xl text-white/60 group-hover:text-white transition-colors"></i>
            </Link>
          </div>
          
          <div className="mt-12 sm:mt-16 flex flex-col sm:flex-row items-center justify-center gap-3">
            <div className="flex -space-x-2">
              {anonymousIcons.map((avatarUrl, idx) => (
                <div 
                  key={idx}
                  className="w-10 h-10 rounded-full border-2 border-white shadow-md overflow-hidden"
                >
                  <img 
                    src={avatarUrl} 
                    alt={`ユーザー${idx + 1}`}
                    className="w-full h-full object-cover"
                  />
                </div>
              ))}
            </div>
            <p className="text-white text-sm font-medium">現在<span className="text-lg font-bold mx-1">76</span>名のご家族が利用しています</p>
          </div>
        </div>
      </section>

      {/* スマイル・ポートとは */}
      <section id="about" className="py-12 sm:py-20 lg:py-32 bg-gradient-to-b from-white to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              スマイル・ポートとは
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto px-4">
              医療的ケア児を育てる保護者が、匿名性を担保された安全な環境で<br className="hidden sm:block" />
              悩みを発信・共有できるコミュニティです。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 sm:gap-12 items-center mb-12 sm:mb-20">
            <div>
              <img 
                src="https://readdy.ai/api/search-image?query=Warm%20supportive%20community%20circle%20of%20diverse%20people%20holding%20hands%20in%20gentle%20pastel%20colors%20soft%20lighting%20peaceful%20atmosphere%20minimalist%20artistic%20illustration%20style&width=800&height=600&seq=community&orientation=landscape" 
                alt="コミュニティ"
                className="w-full h-auto rounded-2xl sm:rounded-3xl shadow-xl"
              />
            </div>
            <div className="px-4 sm:px-0">
              <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                孤立している<br />あなたのために
              </h3>
              <div className="space-y-3 sm:space-y-4 text-sm sm:text-base text-gray-700 leading-relaxed">
                <p className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-500 text-xl sm:text-2xl mt-1"></i>
                  <span><strong>知識がない</strong>と不安なあなたへ：お守りマニュアルで先輩ママの知恵を共有</span>
                </p>
                <p className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-500 text-xl sm:text-2xl mt-1"></i>
                  <span><strong>相談先がない</strong>と感じるあなたへ：スマイル・パートナーが寄り添います</span>
                </p>
                <p className="flex items-start gap-3">
                  <i className="ri-check-line text-teal-500 text-xl sm:text-2xl mt-1"></i>
                  <span><strong>一人で抱え込んでいる</strong>あなたへ：匿名掲示板で安心して気持ちを吐き出せます</span>
                </p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-rose-100 to-purple-100 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12 text-center">
            <i className="ri-shield-check-line text-rose-500 text-3xl sm:text-4xl lg:text-5xl mb-4 sm:mb-6"></i>
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-3 sm:mb-4">
              心理的安全性を最優先
            </h3>
            <p className="text-sm sm:text-base lg:text-lg text-gray-700 leading-relaxed max-w-2xl mx-auto px-4">
              ニックネーム制で完全匿名。本名は一切表示されません。<br className="hidden sm:block" />
              「ここはあなたの避難所です」「否定されることはありません」<br className="hidden sm:block" />
              安心してあなたの気持ちを話してください。
            </p>
          </div>
        </div>
      </section>

      {/* 機能紹介 */}
      <section id="features" className="py-12 sm:py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              3つの機能で<br />あなたをサポート
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-gray-50 to-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 hover:shadow-2xl transition-all duration-300 border border-gray-100"
              >
                <div className={`w-14 h-14 sm:w-16 sm:h-16 flex items-center justify-center rounded-2xl bg-${feature.color}-100 mb-4 sm:mb-6`}>
                  <i className={`${feature.icon} text-${feature.color}-600 text-2xl sm:text-3xl`}></i>
                </div>
                <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 sm:mb-4">{feature.title}</h3>
                <p className="text-sm sm:text-base text-gray-600 leading-relaxed">{feature.description}</p>
                <Link
                  to={index === 0 ? '/board' : index === 1 ? '/manual' : '/partner/recruit'}
                  className={`inline-flex items-center gap-2 mt-4 sm:mt-6 text-${feature.color}-600 font-semibold hover:gap-3 transition-all cursor-pointer whitespace-nowrap`}
                >
                  詳しく見る
                  <i className="ri-arrow-right-line"></i>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* スマイル・パートナー紹介 */}
      <section className="py-12 sm:py-20 lg:py-32 bg-gradient-to-b from-white to-purple-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 bg-purple-100 text-purple-700 px-4 py-2 rounded-full mb-4 sm:mb-6 whitespace-nowrap text-sm sm:text-base">
              <i className="ri-user-star-line"></i>
              <span className="font-semibold">Smile Partner</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              スマイル・パートナーが<br />あなたに寄り添います
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto px-4">
              看護師、保育士、経験豊富な先輩ママなど、<br className="hidden sm:block" />
              専門家があなたの「伴走者」として共感とアドバイスを提供します。
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 sm:gap-6 mb-8 sm:mb-12">
            {/* 看護師 */}
            <Link to="/partners" onClick={handlePartnerLinkClick} className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg text-center hover:shadow-xl transition-all cursor-pointer group">
              <div className="flex justify-center -space-x-3 mb-4">
                {nursePartners.slice(0, 3).map((partner, idx) => (
                  <div key={partner.id} className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-3 border-white shadow-md" style={{ zIndex: 3 - idx }}>
                    <img 
                      src={partner.avatar_url} 
                      alt={partner.nickname}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-2 mb-2">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">看護師</h3>
                <span className="bg-teal-100 text-teal-700 text-xs font-semibold px-2 py-0.5 rounded-full">{nursePartners.length}名</span>
              </div>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                医療の専門知識でサポート
              </p>
              <div className="mt-3 text-teal-600 text-xs sm:text-sm font-medium group-hover:underline">
                詳しく見る →
              </div>
            </Link>

            {/* 保育士 */}
            <Link to="/partners" onClick={handlePartnerLinkClick} className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg text-center hover:shadow-xl transition-all cursor-pointer group">
              <div className="flex justify-center -space-x-3 mb-4">
                {teacherPartners.slice(0, 3).map((partner, idx) => (
                  <div key={partner.id} className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-3 border-white shadow-md" style={{ zIndex: 3 - idx }}>
                    <img 
                      src={partner.avatar_url} 
                      alt={partner.nickname}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-2 mb-2">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">保育士</h3>
                <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-0.5 rounded-full">{teacherPartners.length}名</span>
              </div>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                子育ての実践アドバイス
              </p>
              <div className="mt-3 text-teal-600 text-xs sm:text-sm font-medium group-hover:underline">
                詳しく見る →
              </div>
            </Link>

            {/* 先輩ママ */}
            <Link to="/partners" onClick={handlePartnerLinkClick} className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg text-center hover:shadow-xl transition-all cursor-pointer group">
              <div className="flex justify-center -space-x-3 mb-4">
                {momPartners.slice(0, 3).map((partner, idx) => (
                  <div key={partner.id} className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-3 border-white shadow-md" style={{ zIndex: 3 - idx }}>
                    <img 
                      src={partner.avatar_url} 
                      alt={partner.nickname}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-2 mb-2">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">先輩ママ</h3>
                <span className="bg-rose-100 text-rose-700 text-xs font-semibold px-2 py-0.5 rounded-full">{momPartners.length}名</span>
              </div>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                経験に基づく共感と知恵
              </p>
              <div className="mt-3 text-teal-600 text-xs sm:text-sm font-medium group-hover:underline">
                詳しく見る →
              </div>
            </Link>

            {/* 理学療法士 */}
            <Link to="/partners" onClick={handlePartnerLinkClick} className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg text-center hover:shadow-xl transition-all cursor-pointer group">
              <div className="flex justify-center -space-x-3 mb-4">
                {ptPartners.slice(0, 3).map((partner, idx) => (
                  <div key={partner.id} className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-3 border-white shadow-md" style={{ zIndex: 3 - idx }}>
                    <img 
                      src={partner.avatar_url} 
                      alt={partner.nickname}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-2 mb-2">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">理学療法士</h3>
                <span className="bg-indigo-100 text-indigo-700 text-xs font-semibold px-2 py-0.5 rounded-full">{ptPartners.length}名</span>
              </div>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                発達と運動の専門家
              </p>
              <div className="mt-3 text-teal-600 text-xs sm:text-sm font-medium group-hover:underline">
                詳しく見る →
              </div>
            </Link>

            {/* 社会福祉士 */}
            <Link to="/partners" onClick={handlePartnerLinkClick} className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg text-center hover:shadow-xl transition-all cursor-pointer group">
              <div className="flex justify-center -space-x-3 mb-4">
                {swPartners.slice(0, 3).map((partner, idx) => (
                  <div key={partner.id} className="w-12 h-12 sm:w-14 sm:h-14 rounded-full overflow-hidden border-3 border-white shadow-md" style={{ zIndex: 3 - idx }}>
                    <img 
                      src={partner.avatar_url} 
                      alt={partner.nickname}
                      className="w-full h-full object-cover"
                    />
                  </div>
                ))}
              </div>
              <div className="flex items-center justify-center gap-2 mb-2">
                <h3 className="text-base sm:text-lg font-bold text-gray-900">社会福祉士</h3>
                <span className="bg-orange-100 text-orange-700 text-xs font-semibold px-2 py-0.5 rounded-full">{swPartners.length}名</span>
              </div>
              <p className="text-gray-600 text-xs sm:text-sm leading-relaxed">
                福祉制度と生活支援
              </p>
              <div className="mt-3 text-teal-600 text-xs sm:text-sm font-medium group-hover:underline">
                詳しく見る →
              </div>
            </Link>
          </div>

          <div className="text-center mb-8 sm:mb-12">
            <Link to="/partners" onClick={handlePartnerLinkClick} className="inline-flex items-center gap-2 bg-gradient-to-r from-teal-500 to-teal-600 text-white px-6 sm:px-8 py-2.5 sm:py-3 rounded-full font-semibold hover:shadow-lg transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base">
              すべてのパートナーを見る
              <i className="ri-arrow-right-line"></i>
            </Link>
          </div>

          <div className="bg-gradient-to-br from-purple-100 to-pink-100 rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-12">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8 items-center">
              <div>
                <h3 className="text-xl sm:text-2xl lg:text-3xl font-bold text-gray-900 mb-4 sm:mb-6">
                  私たちの方針
                </h3>
                <div className="space-y-3 sm:space-y-4">
                  <div className="flex items-start gap-3">
                    <i className="ri-heart-3-fill text-rose-500 text-2xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">共感を最優先</h4>
                      <p className="text-gray-700 text-sm">「正論」ではなく「その気持ち、わかるよ」を大切にします。</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-hand-heart-line text-purple-500 text-2xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">心の荷降ろしを手伝う</h4>
                      <p className="text-gray-700 text-sm">まずは話を聞き、気持ちを受け止めることから始めます。</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-3">
                    <i className="ri-time-line text-teal-500 text-2xl mt-1"></i>
                    <div>
                      <h4 className="font-bold text-gray-900 mb-1">見守り時間</h4>
                      <p className="text-gray-700 text-sm">平日 10:00〜17:00 を中心に、パートナーが見守っています。</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-white rounded-xl sm:rounded-2xl p-6 sm:p-8">
                <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">避けること</h4>
                <ul className="space-y-2 sm:space-y-3">
                  <li className="flex items-start gap-2 text-gray-700">
                    <i className="ri-close-circle-line text-gray-400 mt-1"></i>
                    <span className="text-xs sm:text-sm">いきなりのアドバイス</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-700">
                    <i className="ri-close-circle-line text-gray-400 mt-1"></i>
                    <span className="text-xs sm:text-sm">「頑張って」などの安易な励まし</span>
                  </li>
                  <li className="flex items-start gap-2 text-gray-700">
                    <i className="ri-close-circle-line text-gray-400 mt-1"></i>
                    <span className="text-xs sm:text-sm">正論や一般論の押し付け</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 利用者の声 */}
      <section id="voices" className="py-12 sm:py-20 lg:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              利用者の声
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600">
              スマイル・ポートで心が軽くなった家族の体験談
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8">
            {voices.map((voice, index) => (
              <div
                key={index}
                className="bg-gradient-to-br from-teal-50 to-rose-50 rounded-2xl sm:rounded-3xl p-6 sm:p-8 hover:shadow-xl transition-all"
              >
                <div className="flex items-center gap-3 sm:gap-4 mb-4 sm:mb-6">
                  <img 
                    src={voice.avatar} 
                    alt={voice.name}
                    className="w-14 h-14 sm:w-16 sm:h-16 rounded-full object-cover border-4 border-white shadow-md"
                  />
                  <div>
                    <h4 className="font-bold text-gray-900 text-sm sm:text-base">{voice.name}</h4>
                    <p className="text-xs sm:text-sm text-gray-600">{voice.role}</p>
                  </div>
                </div>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed italic">
                  「{voice.comment}」
                </p>
                <div className="flex gap-1 mt-4 sm:mt-6">
                  <i className="ri-star-fill text-amber-400 text-sm sm:text-base"></i>
                  <i className="ri-star-fill text-amber-400 text-sm sm:text-base"></i>
                  <i className="ri-star-fill text-amber-400 text-sm sm:text-base"></i>
                  <i className="ri-star-fill text-amber-400 text-sm sm:text-base"></i>
                  <i className="ri-star-fill text-amber-400 text-sm sm:text-base"></i>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-20 lg:py-32 bg-gradient-to-br from-teal-500 to-teal-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 text-center">
          <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-6 sm:mb-8">
            <i className="ri-ship-line text-white text-3xl sm:text-4xl"></i>
          </div>
          <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-white mb-4 sm:mb-6">
            一人で抱え込まないで
          </h2>
          <p className="text-base sm:text-lg lg:text-xl text-white/90 mb-8 sm:mb-12 leading-relaxed px-4">
            ここには、あなたの気持ちを分かってくれる仲間がいます。<br className="hidden sm:block" />
            今すぐスマイル・ポートに参加して、心を軽くしませんか？
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-3 sm:gap-4 px-4">
            <Link to="/board" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-white text-teal-700 px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap shadow-xl">
              みんなの休憩室へ
              <i className="ri-arrow-right-line text-lg sm:text-xl"></i>
            </Link>
            <Link to="/manual" className="w-full sm:w-auto inline-flex items-center justify-center gap-3 bg-teal-600/50 backdrop-blur-sm border-2 border-white text-white px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg font-semibold hover:bg-teal-600/70 transition-all cursor-pointer whitespace-nowrap">
              お守りマニュアル
              <i className="ri-book-open-line text-lg sm:text-xl"></i>
            </Link>
          </div>
        </div>
      </section>

      {/* 宮城県の支援機関 */}
      <section className="py-12 sm:py-20 lg:py-32 bg-gradient-to-b from-white to-teal-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12 sm:mb-16">
            <div className="inline-flex items-center gap-2 bg-teal-100 text-teal-700 px-4 py-2 rounded-full mb-4 sm:mb-6 whitespace-nowrap text-sm sm:text-base">
              <i className="ri-government-line"></i>
              <span className="font-semibold">Support Organizations</span>
            </div>
            <h2 className="text-2xl sm:text-3xl lg:text-5xl font-bold text-gray-900 mb-4 sm:mb-6">
              宮城県の支援機関
            </h2>
            <p className="text-base sm:text-lg lg:text-xl text-gray-600 leading-relaxed max-w-3xl mx-auto px-4">
              医療的ケア児と家族を支援する宮城県内の公的機関をご紹介します。
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
            {/* みやぎスマイルケアネットワーク */}
            <a 
              href="https://miyagi-smile-care.net/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
            >
              <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-teal-100 rounded-xl">
                  <i className="ri-heart-pulse-line text-teal-600 text-xl sm:text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base group-hover:text-teal-600 transition-colors">
                    みやぎスマイルケアネットワーク
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                    医療的ケア児と家族を支える地域ネットワーク
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-teal-600 text-xs sm:text-sm font-medium">
                <span>公式サイトへ</span>
                <i className="ri-external-link-line"></i>
              </div>
            </a>

            {/* 宮城県医療的ケア児等支援センター */}
            <a 
              href="https://www.pref.miyagi.jp/soshiki/syoufuku/iryoutekicare-center.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
            >
              <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-teal-100 rounded-xl">
                  <i className="ri-hospital-line text-teal-600 text-xl sm:text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base group-hover:text-teal-600 transition-colors">
                    宮城県医療的ケア児等支援センター
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                    医療的ケア児と家族への総合的な相談支援
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-teal-600 text-xs sm:text-sm font-medium">
                <span>公式サイトへ</span>
                <i className="ri-external-link-line"></i>
              </div>
            </a>

            {/* 宮城県発達障害者支援センター えくぼ */}
            <a 
              href="https://www.pref.miyagi.jp/soshiki/syoufuku/ekubo.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
            >
              <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-amber-100 rounded-xl">
                  <i className="ri-heart-pulse-line text-amber-600 text-xl sm:text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base group-hover:text-teal-600 transition-colors">
                    宮城県発達障害者支援センター えくぼ
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                    発達障害に関する相談・支援
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-teal-600 text-xs sm:text-sm font-medium">
                <span>公式サイトへ</span>
                <i className="ri-external-link-line"></i>
              </div>
            </a>

            {/* 宮城県障害者総合相談センター */}
            <a 
              href="https://www.pref.miyagi.jp/soshiki/syoufuku/soudan-center.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
            >
              <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-rose-100 rounded-xl">
                  <i className="ri-customer-service-2-line text-rose-600 text-xl sm:text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base group-hover:text-teal-600 transition-colors">
                    宮城県障害者総合相談センター
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                    障害に関する総合的な相談窓口
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-teal-600 text-xs sm:text-sm font-medium">
                <span>公式サイトへ</span>
                <i className="ri-external-link-line"></i>
              </div>
            </a>

            {/* 仙台市障害者総合支援センター */}
            <a 
              href="https://www.city.sendai.jp/shogaishien/kurashi/kenkotofukushi/shogaisha/shien/center.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
            >
              <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-indigo-100 rounded-xl">
                  <i className="ri-building-line text-indigo-600 text-xl sm:text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base group-hover:text-teal-600 transition-colors">
                    仙台市障害者総合支援センター
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                    仙台市の障害者支援の総合窓口
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-teal-600 text-xs sm:text-sm font-medium">
                <span>公式サイトへ</span>
                <i className="ri-external-link-line"></i>
              </div>
            </a>

            {/* 宮城県小児在宅医療推進事業 */}
            <a 
              href="https://www.pref.miyagi.jp/soshiki/iryou/syounizaitaku.html" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
            >
              <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-purple-100 rounded-xl">
                  <i className="ri-home-heart-line text-purple-600 text-xl sm:text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base group-hover:text-teal-600 transition-colors">
                    宮城県小児在宅医療推進事業
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                    小児在宅医療の推進と支援
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-teal-600 text-xs sm:text-sm font-medium">
                <span>公式サイトへ</span>
                <i className="ri-external-link-line"></i>
              </div>
            </a>

            {/* 宮城県保健福祉部 障害福祉課 */}
            <a 
              href="https://www.pref.miyagi.jp/soshiki/syoufuku/" 
              target="_blank" 
              rel="noopener noreferrer"
              className="bg-white rounded-2xl p-5 sm:p-6 shadow-lg hover:shadow-xl transition-all cursor-pointer group"
            >
              <div className="flex items-start gap-3 sm:gap-4 mb-3 sm:mb-4">
                <div className="w-12 h-12 sm:w-14 sm:h-14 flex items-center justify-center bg-emerald-100 rounded-xl">
                  <i className="ri-government-line text-emerald-600 text-xl sm:text-2xl"></i>
                </div>
                <div className="flex-1">
                  <h3 className="font-bold text-gray-900 mb-1 text-sm sm:text-base group-hover:text-teal-600 transition-colors">
                    宮城県保健福祉部 障害福祉課
                  </h3>
                  <p className="text-xs sm:text-sm text-gray-600 leading-relaxed">
                    障害福祉施策の総合窓口
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 text-teal-600 text-xs sm:text-sm font-medium">
                <span>公式サイトへ</span>
                <i className="ri-external-link-line"></i>
              </div>
            </a>
          </div>
        </div>
      </section>

      {/* 関連リンク */}
      <RelatedLinks />

      {/* フッター */}
      <footer className="bg-gradient-to-r from-teal-700 to-teal-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8 sm:gap-12 mb-8 sm:mb-12">
            <div className="sm:col-span-2">
              <div className="flex items-center gap-3 mb-4 sm:mb-6">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-white/20 rounded-full flex items-center justify-center">
                  <i className="ri-ship-line text-white text-xl sm:text-2xl"></i>
                </div>
                <span className="text-xl sm:text-2xl font-bold">みやぎスマイル・ポート</span>
              </div>
              <p className="text-teal-100 text-base sm:text-lg mb-3 sm:mb-4">〜医療的ケア児と家族の港〜</p>
              <p className="text-teal-200 text-xs sm:text-sm leading-relaxed">
                ここはあなたの避難所です。否定されることはありません。<br />
                安心してお話しください。
              </p>
            </div>
            <div>
              <h4 className="font-bold text-base sm:text-lg mb-3 sm:mb-4">サイトマップ</h4>
              <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-teal-100">
                <li><a href="#home" className="hover:text-white transition-colors cursor-pointer">ホーム</a></li>
                <li><a href="#about" className="hover:text-white transition-colors cursor-pointer">スマイル・ポートとは</a></li>
                <li><Link to="/board" className="hover:text-white transition-colors cursor-pointer">みんなの休憩室</Link></li>
                <li><Link to="/manual" className="hover:text-white transition-colors cursor-pointer">お守りマニュアル</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold text-base sm:text-lg mb-3 sm:mb-4">お問い合わせ</h4>
              <ul className="space-y-2 sm:space-y-3 text-xs sm:text-sm text-teal-100">
                <li className="flex items-center gap-2">
                  <i className="ri-mail-line"></i>
                  <span>support@smile-port.jp</span>
                </li>
                <li className="flex items-center gap-2">
                  <i className="ri-time-line"></i>
                  <span>平日 10:00〜17:00</span>
                </li>
              </ul>
            </div>
          </div>
          <div className="border-t border-teal-600 pt-6 sm:pt-8 flex flex-col sm:flex-row items-center justify-between gap-3 sm:gap-4">
            <p className="text-xs sm:text-sm text-teal-100">
              &copy; 2025 みやぎスマイル・ポート. All rights reserved.
            </p>
            <div className="flex items-center gap-4 sm:gap-6">
              <a href="https://readdy.ai/?ref=logo" target="_blank" rel="noopener noreferrer" className="text-xs sm:text-sm text-teal-100 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                Powered by Readdy
              </a>
              <a href="#" className="text-xs sm:text-sm text-teal-100 hover:text-white transition-colors cursor-pointer whitespace-nowrap">
                プライバシーポリシー
              </a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
